/**
 * @file Publish the patient an OPEN TEST ORDER belongs to onto the Xoe focus bus.
 * @module hooks/useXoeTestOrderFocus
 *
 * The sibling of `PatientModal`'s publish effect (86d3z0ty3 / X1). Opening a test order is
 * the same kind of act as opening a patient record — the practitioner is now looking at one
 * specific patient — so Xoe should follow it and bind the conversation the same way.
 *
 * ── WHY A HOOK AND NOT A PUBLISH EFFECT INSIDE `CreateTestModal` ──────────────────────────
 * X1 made `PatientModal` the single publisher precisely so no render site could be missed,
 * and the obvious mirror here would be to publish from inside the test-order modal, which
 * all six of its render sites share. **That is wrong, and the reason is the teardown, not
 * the publish.**
 *
 * Two of those six sites — `TestingTab` and `MedicalDashboard` — render the test-order modal
 * from *inside* `PatientModal`, for the patient `PatientModal` has already published. Both
 * publishers would then hold the SAME `patientId`, so closing the inner test order would run
 * `clearPatientFocus(P)`, which clears on an id match — wiping the focus while the patient
 * record is still open behind it. `PatientModal`'s effect would not re-run (its deps have not
 * changed), so Xoe would silently fall back to a general conversation with the record still
 * on screen. Publishing from the two pages that own a *standalone* test order avoids the
 * nesting entirely, and the shared hook keeps it one implementation rather than two.
 *
 * ⚠️ **`sectionKey` is deliberately NOT published.** `XOE_SECTION_LABELS` is a `PatientModal`
 * TAB key map; a test-order drawer is not a tab, and inventing a key would mean hand-mirroring
 * it into `xoe-agent/app/agent/page_context.py`, which has no mechanical guard against the two
 * lists drifting. The route label already carries the screen ("Test orders" / "Results in
 * tray"), which is the part the agent needs.
 */

import { useEffect } from "react";

import { useXoeFocusPublisher } from "../../context/XoeFocusContext";
import { formatPatientPrimaryDisplayName } from "../../pages/healthcare/patient/patientNames/patientNameDisplay";
import type { Test } from "../../types/healthcare/TestTypes";

/**
 * Bind Xoe to the patient a test order is for, while that order is open.
 *
 * @param isOpen  whether the test-order modal is currently open
 * @param test    the order in scope; `patient_id` is the AUTHENTICATION service's `users.id`
 *                (healthcare's `test_orders.patient_id` is resolved against it by
 *                `TestController::indexCompany()` via auth's `patients/bulk`), which is the
 *                same id space as `xoe_conversations.patient_id`
 */
export const useXoeTestOrderFocus = (
	isOpen: boolean,
	test: Partial<Test> | null | undefined,
): void => {
	const { setPatientFocus, clearPatientFocus } = useXoeFocusPublisher();

	// `patient_id` is always present on a real order; `patient` is hydration-dependent and is
	// legitimately null for the WHOLE page when healthcare's bulk lookup against auth fails
	// (`TestController::indexCompany()`). So the id is the binding and the label is
	// best-effort — never derive the id from `test.patient`, which would stop binding in
	// exactly the degraded state where a practitioner still needs the assistant.
	const focusPatientId = test?.patient_id;
	const focusPatientLabel = test?.patient
		? formatPatientPrimaryDisplayName(test.patient)
		: undefined;

	useEffect(() => {
		// A falsy id is not an edge case: "New test order" seeds an empty `patient_id` before a
		// patient has been chosen, and publishing that would bind the conversation to a
		// patient who does not exist.
		if (!isOpen || !focusPatientId) return;

		setPatientFocus({ patientId: focusPatientId, patientLabel: focusPatientLabel });

		// Clears on close, on unmount and when a different order is opened — and only while
		// this order's patient is still the focused record, so it cannot wipe a focus another
		// publisher has since taken.
		return () => clearPatientFocus(focusPatientId);
		// The label dep is the derived STRING, not `test.patient` — the object's identity
		// changes on every list refetch, which would re-publish on each poll.
	}, [isOpen, focusPatientId, focusPatientLabel, setPatientFocus, clearPatientFocus]);
};
